import { IOperator } from 'api/types/_operator';
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from 'common/toast';
import LabelWrapper from 'components/Common/LabelWrapper';
import ModalConfirm from 'components/Common/ModalConfirm';
import TooltipCustom from 'components/Common/TooltipCustom';
import { useRole } from 'components/Hooks/UserHooks';
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { ROLES_FOR_APP, isHavePermissionRole } from 'helpers/role';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from 'react-countup';
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardBody, CardHeader, Col, Container, Input, Row } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault, } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import TableContainer from "../../../components/Common/TableContainer";
import VOCReasonForm from './VOCReasonForm';
import { formatDateStringToKorea } from 'helpers/format';
import CopyWrapper from 'components/Common/CopyWrapper';
import DropdownStatusNew from 'components/Common/DropdownStatusNew';
import { COLOR_IS_REQUIRED_CONTENT, COLOR_VOC_TYPE, LIST_IS_REQUIRED_CONTENT_OPTION, LIST_VOC_TYPE_OPTION } from 'helpers/constans';
import DatePickerCustom from 'components/Common/DatePickerCustom';
import moment from 'moment';
import { deleteReason, getVOCReasons, putReason } from 'store/voc-reason/thunk';


registerLocale('en', en);
registerLocale('ko', ko);

const VOCReasons = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const navigate = useNavigate();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    type: withDefault(NumberParam, null),
    start_date: withDefault(StringParam, ""),
    end_date: withDefault(StringParam, ""),
    sort_by: withDefault(StringParam, ''),
    keyword: withDefault(StringParam, ''),
  });

  const [keywordSearch, setKeywordSearch] = useState<string>(
    query?.keyword || ""
  );
  const [vocTypeSearch, setVOCTypeSearch] = useState<string>(query?.from_role || "");

  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.start_date
      ? moment(query?.start_date || "", "Y-MM-DD").toDate()
      : null,
    query?.end_date ? moment(query?.end_date || "", "Y-MM-DD").toDate() : null,
  ]);
  const [startDate, endDate] = dateSearch;

  const [userEdit, setUserEdit] = useState<IOperator & { isCopy?: boolean } | null>(null);

  const [isOpenConfirm, setIsConfirm] = useState<boolean>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [initialValuesDefault, setInitialValuesDefault] = useState<IOperator | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.VOCReasons;

  const VOCReasonProperties = createSelector(
    selectLayoutState,
    (state) => ({
      vocReasons: state.vocReasons,
      isVOCReasonSuccess: state.isVOCReasonSuccess,
      isVOCReasonLoading: state.isVOCReasonLoading,
      error: state.error,
    })
  );

  // Inside your component
  const { vocReasons, error, isVOCReasonLoading } = useSelector(VOCReasonProperties);


  useEffect(() => {
    dispatch(getVOCReasons(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      keyword: keywordSearch,
      type: vocTypeSearch,
      start_date: startDate
        ? moment(new Date(startDate)).format("Y-MM-DD")
        : "",
      end_date: endDate
        ? moment(new Date(endDate)).format("Y-MM-DD")
        : "",
      page: 1,
      time_request: + new Date()
    });
  };

  const resetData = () => {
    setQuery({
      keyword: "",
      type: '',
      start_date: "",
      end_date: "",
      sort_by: '',
      time_request: + new Date(),
      page: 1,
    }, "push")
    setKeywordSearch((_prev) => "");
    setVOCTypeSearch((_prev) => "");
    setDateSearch([null, null]);
  };

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  // Begin::Update User
  const onCloseClick = () => {
    setUserEdit((_prev) => null);
    setIsOpen((_prev) => false)
  }

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(getVOCReasons(query));
  }
  //End::Update User

  // Begin::Delete
  const handleConfirmDelete = (item: any) => {
    setInitialValuesDefault((_prev) => item);
    setIsConfirm((_prev) => true);
  };

  const handleActionDelete = async () => {
    if (!(initialValuesDefault?.id)) { return; };
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await deleteReason(initialValuesDefault?.id);

      if (response?.code === 200) {
        dispatch(getVOCReasons(query));
        setIsConfirmLoading((_prev) => false);
        setIsConfirm((_prev) => false);
        setInitialValuesDefault((_prev) => null);
        toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseConfirmClick = () => {
    setInitialValuesDefault((_prev) => null);
    setIsConfirm((_prev) => false);
  }
  // End::Delete

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        thClass: "align-middle",
        Cell: ({ ...props }) => {
          const item = props?.row?.original;
          return (
            <CopyWrapper contentCopy={item?.id || ''} style={{ display: 'inline-flex', minWidth: '50px' }}>
              <div>
                {vocReasons?.pagination?.total -
                  ((query?.page - 1) * query?.limit + props?.row?.index)}
              </div>
            </CopyWrapper>
          );
        },
      },
      {
        Header: t('Status'),
        accessor: "status",
        filterable: false,
        sortable: false,
        thClass: 'text-center',
        thWidth: 100,
        Cell: (cell: any) => {
          const value: any = cell?.row?.original;
          const active = cell?.value;
          return (
            <div className="text-center form-check form-switch form-switch-sm" dir="ltr" style={{ minWidth: '50px' }}>
              <input type="checkbox" className="form-check-input" name="is_active" id="customSwitchsizelg" checked={Boolean(active)}
                onChange={(event: any) => {
                  if (isHavePermissionRole(ROLES_FOR_APP.OPERATOR_UPDATE, userPermissions)) {
                    handleChangeStatusItem(value, event.target.checked)
                  }
                }}
              />
            </div>
          );
        }
      },
      {
        Header: t('VOC Type'),
        accessor: "type",
        thClass: 'text-center',
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_VOC_TYPE[cell?.value] || "secondary"
                  }`}
              >
                {t(
                  LIST_VOC_TYPE_OPTION?.find(
                    (item: any) => item?.value === cell?.value
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t('Reason (EN)'),
        accessor: "reason_en",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (<>
            <div style={{ minWidth: '100px' }}>{cell?.value || '-'}</div>
          </>)
        },
      },
      {
        Header: t('Reason (KR)'),
        accessor: "reason_kr",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (<>
            <div style={{ minWidth: '100px' }}>{cell?.value || '-'}</div>
          </>)
        },
      },
      {
        Header: t('Is Require Detail'),
        accessor: "is_required_content",
        thClass: "text-center",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {

          return (<div className="text-center" style={{ minWidth: 80 }}>
            <span
              className={`rounded-pill badge bg-${COLOR_IS_REQUIRED_CONTENT[cell?.value] || "secondary"
                }`}
            >
              {t(
                LIST_IS_REQUIRED_CONTENT_OPTION?.find(
                  (item: any) => item?.value === cell?.value
                )?.label || ""
              )}
            </span>
          </div>)
        },
      },
      {
        Header: t('Order'),
        accessor: "sort",
        thClass: "text-end",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (<>
            <div style={{ minWidth: '40px' }} className='text-end'>{cell?.value || '-'}</div>
          </>)
        },
      },
      {
        Header: t('Created at'),
        accessor: "created_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: 'text-end',
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || '');
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: '100px' }}>
              <span><span>{arrDate?.[0] || ''}</span> <br /> <span className="text-secondary">{arrDate?.[1] || ''}</span> </span>
            </div>
          )
        },
      },
      {
        Header: t('Updated at'),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: 'text-end',
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || '');
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: '100px' }}>
              <span><span>{arrDate?.[0] || ''}</span> <br /> <span className="text-secondary">{arrDate?.[1] || ''}</span> </span>
            </div>
          )
        },
      },
      {
        Header: t('Action'),
        thClass: 'text-center',
        thWidth: 100,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0" style={{ minWidth: '70px' }}>
              {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_UPDATE, userPermissions) && (
                <TooltipCustom
                  title={t('Button Update Reason')}
                  id={`update-us-${item?.id}`}
                >
                  <Link className="btn btn-sm btn-soft-primary edit-item-btn" to="#"
                    onClick={(e) => { 
                      setIsOpen(true);
                      setUserEdit(item)
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom"></i>
                  </Link>
                </TooltipCustom>
              )}
              {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_DELETE, userPermissions) && (
                <TooltipCustom
                  title={t('Button Delete Reason')}
                  id={`detail-cp-${item?.id}`}
                >
                    <Link className="btn btn-sm btn-soft-danger edit-item-btn" to="#"
                      onClick={(e) => { e.preventDefault(); handleConfirmDelete(item) }}
                    >
                      <i className="ri-delete-bin-3-fill align-bottom"></i>
                    </Link>
                </TooltipCustom>
              )}
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, query, userPermissions, vocReasons?.pagination?.total]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return ({ ..._prev, ...sortBy });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeStatusItem = async (item: any, status: boolean) => {
    try {
      const { reason_en, reason_kr } = structuredClone(item);
      const data = {
        reason_en,
        reason_kr,
        status: Number(status),
      };

      const response: any = await putReason(item?.id, data);

      if (response?.data) {
        dispatch(getVOCReasons(query));
        toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  }


  useEffect(() => {
    document.title = `${t('VOC Reason')} - ${t('Home')} | Baive`;
    document.body.classList.remove('vertical-sidebar-enable');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('VOC Reason')} pageTitle={t('Home')} />
          <Row>
            {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_LIST, userPermissions) && (
              <Col lg={12}>
                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }}>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-5 con-sm-6">
                        <div>
                          <h5 className="card-title mb-0">
                            <span className="me-2">{t('Total')}:</span>
                            <CountUp
                              start={0}
                              end={vocReasons?.pagination?.total || 0}
                              duration={1}
                              className="text-primary"
                            />
                          </h5>
                        </div>
                      </div>
                      <div className="col-7 con-sm-6 text-end">
                        {isHavePermissionRole(ROLES_FOR_APP.OPERATOR_CREATE, userPermissions) && (<div className="col-sm-auto">
                          <div>
                            <button
                              type="button"
                              className="btn btn-success add-btn"
                              id="create-btn"
                              onClick={() => setIsOpen((_prev) => true)}
                            >
                              <i className="ri-add-line align-bottom me-1"></i>
                              {t('Button Create Reason')}
                            </button>
                          </div>
                        </div>)}
                      </div>
                    </Row>
                  </CardHeader>
                  <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                    <Row className="g-4 align-items-center mt-0">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={t("Reason")}
                          isShow={!!keywordSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Reason")}...`}
                            value={keywordSearch}
                            onChange={(e) => setKeywordSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("VOC Type")} isShow={true}>
                          <DropdownStatusNew
                            name="from-role"
                            dataList={LIST_VOC_TYPE_OPTION || []}
                            placeholder={`${t("Select VOC type")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={vocTypeSearch}
                            onChangeSelect={(e: any) =>
                              setVOCTypeSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_VOC_TYPE}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-4 mt-md-2 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col sm={12} md={3} xl={3} xxl={3} className="hstack gap-1 justify-content-center justify-content-sm-end mt-3 mt-md-3">
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-2 "
                            onClick={searchData}
                            disabled={isVOCReasonLoading}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t('Button Search')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t('Button Reset')}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <CardBody className="pt-3">
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={vocReasons?.items?.length ? vocReasons?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={vocReasons?.pagination?.total}
                      customPageCount={Math.ceil(Number(vocReasons?.pagination?.total) / Number(vocReasons?.pagination?.limit))}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isVOCReasonLoading}
                    />
                  </CardBody>
                </Card>
              </Col>
            )}</Row>
        </Container>
        <ToastContainer closeButton={false} newestOnTop={false} pauseOnFocusLoss autoClose={2000} limit={2} />

        {
          isOpen && (
            <VOCReasonForm
              id={String(userEdit?.id || '')}
              item={userEdit}
              triggerRefresh={triggerRefresh}
              onCloseClick={onCloseClick}
            />
          )
        }

        <ModalConfirm
          header={t('Button Delete Reason')}
          isOpen={isOpenConfirm}
          isLoading={isConfirmLoading}
          onClose={onCloseConfirmClick}
          onConfirm={handleActionDelete}
        />
      </div>
    </React.Fragment >
  );
};

export default VOCReasons;